import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Eye, EyeOff, X, Check } from 'lucide-react';
import styles from './AuthForm.module.css';
import * as Yup from 'yup';

interface AuthFormProps {
  initialMode?: 'login' | 'signup';
}

// Toast component
const Toast: React.FC<{ message: string; type: 'error' | 'success'; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 32,
      right: 32, // changed from left: 32
      zIndex: 3000,
      minWidth: 260,
      maxWidth: '90vw',
      background: type === 'error' ? '#fff6f6' : '#f1fff1',
      color: type === 'error' ? '#d32f2f' : '#388e3c',
      border: `1.5px solid ${type === 'error' ? '#ffd6d6' : '#b2ffb2'}`,
      borderRadius: 8,
      padding: '14px 28px',
      fontSize: '1.05rem',
      fontWeight: 500,
      boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      textAlign: 'center',
      animation: 'fadeInToast 0.3s',
    }}>
      {message}
    </div>
  );
};

const signupSchema = Yup.object().shape({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm your password'),
});

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

const AuthForm: React.FC<AuthFormProps> = ({ initialMode = 'login' }) => {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);

  // Toast/fadeIn animation
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `@keyframes fadeInToast { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Clear errors and fields on mode change
  useEffect(() => {
    setErrors({});
    setFormData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
  }, [mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = async () => {
    try {
      if (mode === 'signup') {
        await signupSchema.validate(formData, { abortEarly: false });
      } else {
        await loginSchema.validate(formData, { abortEarly: false });
      }
      setErrors({});
      return true;
    } catch (err: any) {
      const newErrors: { [key: string]: string } = {};
      if (err.inner) {
        err.inner.forEach((e: any) => {
          if (e.path) newErrors[e.path] = e.message;
        });
      }
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);
    const isValid = await validate();
    if (!isValid) return;
    setLoading(true);
    if (mode === 'signup') {
      try {
        const res = await fetch('https://nova-media-backend.vercel.app/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setToast({ message: data.message || 'Signup failed.', type: 'error' });
        } else {
          setToast({ message: 'A verification email has been sent. Please check your inbox and spam folder to verify your account.', type: 'success' });
          setFormData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
          setShowVerificationModal(true);
        }
      } catch (err) {
        setToast({ message: 'Network error. Please try again.', type: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res = await fetch('https://nova-media-backend.vercel.app/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setToast({ message: data.message || 'Login failed.', type: 'error' });
        } else {
          localStorage.setItem('user', JSON.stringify({
            fullName: data.user.fullName,
            email: data.user.email,
            phone: data.user.phone,
            status: data.status || null
          }));
          localStorage.setItem('token', data.token);
          setToast({ message: 'Login successful!', type: 'success' });
          setFormData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
          setTimeout(() => {
            window.location.href = '/';
          }, 1200);
        }
      } catch (err) {
        setToast({ message: 'Network error. Please try again.', type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const closeModal = () => {
    setShowVerificationModal(false);
    setTimeout(() => {
      window.location.href = '/';
    }, 1200);
  };

  return (
    <div className={styles.authContainer}>
      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'success'}
        onClose={() => setToast(null)}
      />
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <div className={styles.tabContainer}>
            <button
              className={`${styles.tabButton} ${mode === 'login' ? styles.active : ''}`}
              onClick={() => setMode('login')}
              type="button"
            >
              Login
            </button>
            <button
              className={`${styles.tabButton} ${mode === 'signup' ? styles.active : ''}`}
              onClick={() => setMode('signup')}
              type="button"
            >
              Sign Up
            </button>
          </div>
          <h1 className={styles.title}>
            {mode === 'login'
              ? 'Login to Your Account'
              : 'Join the Community!'}
          </h1>
          <p className={styles.subtitle}>
            {mode === 'login'
              ? 'Sign in to access the 7-step system trusted by top creators and agencies.'
              : 'Start your influencer journey today'}
          </p>
        </div>
        <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
          {mode === 'signup' && (
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="fullName">
                Full Name
              </label>
              <div className={styles.inputWrapper}>
                <User className={styles.inputIcon} />
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  className={styles.input}
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {errors.fullName && <div className={styles.errorText}>{errors.fullName}</div>}
            </div>
          )}
          {mode === 'signup' && (
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="phone">
                Phone Number
              </label>
              <div className={styles.inputWrapper}>
                <User className={styles.inputIcon} />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className={styles.input}
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {errors.phone && <div className={styles.errorText} style={{ color: '#d32f2f' }}>{errors.phone}</div>}
            </div>
          )}
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <div className={styles.inputWrapper}>
              <Mail className={styles.inputIcon} />
              <input
                type="email"
                id="email"
                name="email"
                className={styles.input}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            {errors.email && <div className={styles.errorText}>{errors.email}</div>}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="password">
              Password
            </label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className={styles.input}
                placeholder={mode === 'login' ? 'Enter your password' : 'Create a password'}
                value={formData.password}
                onChange={handleInputChange}
                required
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            {errors.password && <div className={styles.errorText}>{errors.password}</div>}
          </div>
          {mode === 'signup' && (
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="confirmPassword">
                Confirm Password
              </label>
              <div className={styles.inputWrapper}>
                <Lock className={styles.inputIcon} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={styles.input}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {errors.confirmPassword && <div className={styles.errorText} style={{ color: '#d32f2f' }}>{errors.confirmPassword}</div>}
            </div>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <span className={styles.spinner} style={{ marginRight: 0, border: '2.5px solid #fff', borderTop: '2.5px solid #f4c542', background: 'transparent', display: 'block', margin: '0 auto' }}></span>
            ) : (
              mode === 'login' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>
      </div>
      {showVerificationModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <button className={styles.modalClose} onClick={closeModal}>
              <X />
            </button>
            <div className={styles.modalContent}>
              <div className={styles.modalIcon}>
                <Mail color="white" size={32} />
                <div className={styles.modalIconBadge}>
                  <Check color="white" size={16} />
                </div>
              </div>
              <h2 className={styles.modalTitle}>Verification Email Sent!</h2>
              <p className={styles.modalDescription}>
                We've sent a verification email to your inbox. Please check your email and click the verification link to activate your account.
              </p>
              <div className={styles.modalNote}>
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthForm;